/**
 * ID → name resolution for the admin console.
 *
 * The authz admin RPCs return technical ids for principals and scopes, and
 * enies-lobby deliberately echoes the id/key into the *_display_name fields,
 * leaving naming to the client (see admin_authz.go — "UI resolves friendlier
 * names"). So we join those ids against the catalogs the console already
 * loads: role bundles, the scope tree, groups, and — via one batched
 * ResolveUserNames call per id set — the user directory. Every resolver
 * returns undefined on a miss so callers fall back to the raw id via
 * <ResolvedId>.
 */ import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { userManagementClient } from '../../lib/connect';
import { useListBundles, useScopeTree } from './Access/hooks';
import { useListGroups } from './Groups/hooks';
import { USERS_KEY } from './People/hooks';
/**
 * Resolvers for the four id kinds shown in the admin console. Group, bundle,
 * and scope names come from list queries the console already caches (shared
 * across every surface via react-query). Users are resolved with a single
 * batched ResolveUserNames call per id set; unknown/deleted/foreign-tenant
 * ids are simply absent from the response and fall back to the id.
 */ // AdminListGroups defaults to 50 rows; pull a generous page so the resolver
// covers group-heavy tenants. Beyond this, an unresolved group still degrades
// to its raw id (the accepted fallback).
const GROUP_RESOLVER_LIMIT = 500;
// ResolveUserNames caps at 100 ids per call and rejects over-cap requests
// with InvalidArgument, so a surface with an unbounded principal list (e.g.
// a large group's member roster) is truncated client-side first. Ids past
// the cap fall back to their raw id rather than erroring the whole resolver.
const MAX_USER_RESOLUTIONS = 100;
export function useAdminNames(userIds = [], options = {}) {
    const { enabled = true } = options;
    const { data: groups } = useListGroups('', GROUP_RESOLVER_LIMIT, {
        enabled
    });
    const { data: bundles } = useListBundles(true, {
        enabled
    });
    const { data: scopeNodes } = useScopeTree(0, {
        enabled
    });
    const distinctUserIds = enabled ? [
        ...new Set(userIds.filter(Boolean))
    ].slice(0, MAX_USER_RESOLUTIONS).sort() : [];
    const { data: resolvedUsers } = useQuery({
        // Nested under USERS_KEY (shared with People/hooks) so a user mutation
        // (create/role/status/delete) invalidates stale name resolutions too, the
        // same relationship the old per-id keys had. The id set is part of the
        // key itself, not a param alongside it, so distinct surfaces (and their
        // distinct id sets) never collide with each other or with People's own
        // [USERS_KEY, search, page, pageSize] list query.
        queryKey: [
            USERS_KEY,
            'resolve-names',
            distinctUserIds
        ],
        enabled: enabled && distinctUserIds.length > 0,
        staleTime: 5 * 60000,
        retry: false,
        queryFn: async ()=>{
            try {
                return (await userManagementClient.resolveUserNames({
                    userIds: distinctUserIds
                })).users;
            } catch  {
                // Unresolvable (error) → a cached miss for the whole batch, so every
                // id degrades to the raw id and remounts don't re-fire the lookup.
                return [];
            }
        }
    });
    const userMap = useMemo(()=>new Map((resolvedUsers ?? []).map((u)=>[
                u.id,
                u.displayName
            ])), [
        resolvedUsers
    ]);
    const groupMap = useMemo(()=>new Map((groups ?? []).map((g)=>[
                g.id,
                g.displayName
            ])), [
        groups
    ]);
    const bundleMap = useMemo(()=>new Map((bundles ?? []).map((b)=>[
                b.key,
                b.displayName
            ])), [
        bundles
    ]);
    const scopeMap = useMemo(()=>new Map((scopeNodes ?? []).map((n)=>[
                n.id,
                n.displayName
            ])), [
        scopeNodes
    ]);
    return {
        user: (id)=>userMap.get(id) || undefined,
        group: (id)=>groupMap.get(id) || undefined,
        bundle: (key)=>bundleMap.get(key) || undefined,
        scope: (id)=>scopeMap.get(id) || undefined
    };
}
/**
 * Renders a technical id as its human name with the id preserved in a hover
 * tooltip; when no name resolves it degrades to the raw id in monospace. This
 * is the single place the "name, id-in-tooltip, else id" rule lives.
 */ export function ResolvedId({ id, name, className }) {
    if (!id) return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
    // A name equal to the id is not a real name (the RPC echoed the id), and a
    // whitespace-only name would render a blank label — treat both as unresolved
    // so we fall back to the id rather than a self-referential/empty tooltip.
    if (name?.trim() && name !== id) {
        return /*#__PURE__*/ _jsx("span", {
            className: className,
            title: id,
            "data-resolved-id": id,
            children: name
        });
    }
    // Unresolved machine ids (32-hex) render shortened — a wall of full hashes
    // is unreadable; the full id stays one hover away. Ordinary unresolved ids
    // render as-is with no tooltip (a title equal to the text is noise).
    const shortened = /^[0-9a-f]{32}$/.test(id);
    return /*#__PURE__*/ _jsx("code", {
        className: `text-xs ${className ?? ''}`.trim(),
        title: shortened ? id : undefined,
        "data-resolved-id": id,
        children: shortened ? `${id.slice(0, 8)}…` : id
    });
}
